import React from "react"
import loadable from "@loadable/component"
// Sentry
import * as Sentry from "@sentry/browser"
// Intl
import { IntlProvider } from "react-intl"
import messagesEn from "translations/en.json"
// Router
import { Redirect, Switch } from "react-router-dom"
import AppRoute from "utils/routing/AppRoute"

import { ModalConductor, ModalProvider } from "contexts/ModalContext"
import { SnackbarConductor, SnackbarContextProvider } from "contexts/SnackbarContext"
import { AuthContext, AuthProvider } from "contexts/AuthContext"
import { BreadcrumbContextProvider } from "contexts/BreadcrumbContext"
import { ClipboardContextProvider } from "contexts/ClipboardContext"
import { DownloadContextProvider } from "contexts/DownloadContext"
import { HashtagDrawerContextProvider } from "contexts/HashtagDrawerContext"

import { CaptionContextProvider } from "contexts/CaptionContext"
import { CollectionContextProvider } from "contexts/CollectionContext"
import { HashtagContextProvider } from "contexts/HashtagContext"
import "App.css"
import EventApi from "./utils/api/event"

// Initialize sentry
if (process.env.RAZZLE_APP_SENTRY_KEY) {
  Sentry.init({ dsn: process.env.RAZZLE_APP_SENTRY_KEY })
}

// Pages
const PlanPage = loadable(() => import("pages/account/PlanPage"))
const AccountPage = loadable(() => import("pages/account/AccountPage"))
const SecurityPage = loadable(() => import("pages/account/SecurityPage"))
const CaptionPage = loadable(() => import("pages/CaptionPage"))
const CaptionsPage = loadable(() => import("pages/CaptionsPage"))
const LoginPage = loadable(() => import("pages/auth/LoginPage"))
const SignupPage = loadable(() => import("pages/auth/SignupPage"))
const CollectionsPage = loadable(() => import("pages/CollectionsPage"))
const CollectionPage = loadable(() => import("pages/CollectionPage"))
const ForgotPasswordPage = loadable(() => import("pages/auth/ForgotPasswordPage"))
const ResetPasswordPage = loadable(() => import("pages/auth/ResetPasswordPage"))
const ConfirmAffiliatePage = loadable(() => import("pages/affiliate/ConfirmAffiliatePage"))
const NetworkCallbackPage = loadable(() => import("pages/auth/NetworkCallbackPage"))

const messages = {
  en: messagesEn,
}

// language without region code
const language = typeof window !== "undefined" ? navigator.language.split(/[-_]/)[0] : null

export const routes = [
  {
    id: "login",
    name: "Login",
    path: "/login",
    allow: true,
    authenticated: false,
    component: LoginPage,
    exact: true,
  },
  {
    id: "signup",
    name: "Signup",
    path: "/signup",
    allow: true,
    authenticated: false,
    component: SignupPage,
    exact: true,
  },
  {
    id: "account",
    name: "Account",
    path: "/account",
    allow: false,
    authenticated: true,
    component: AccountPage,
    exact: true,
  },
  {
    id: "security",
    name: "Security",
    path: "/security",
    allow: false,
    authenticated: true,
    component: SecurityPage,
    exact: true,
  },
  {
    id: "plan",
    name: "Plan",
    path: "/plan",
    allow: false,
    authenticated: true,
    component: PlanPage,
    exact: true,
  },
  {
    id: "captions",
    name: "Captions",
    path: "/captions",
    allow: false,
    authenticated: true,
    component: CaptionsPage,
    exact: true,
  },
  {
    id: "new-caption",
    name: "New",
    path: "/captions/new",
    allow: false,
    authenticated: true,
    component: CaptionPage,
    exact: true,
  },
  {
    id: "caption",
    name: "Edit",
    path: "/captions/:captionId",
    allow: false,
    authenticated: true,
    component: CaptionPage,
    exact: true,
  },
  {
    id: "collections",
    name: "Saved Hashtags",
    path: "/collections",
    allow: false,
    authenticated: true,
    component: CollectionsPage,
    exact: true,
  },
  {
    id: "new-collection",
    name: "New",
    path: "/collections/new",
    allow: false,
    authenticated: true,
    component: CollectionPage,
    exact: true,
  },
  {
    id: "collection",
    name: "Edit",
    path: "/collections/:collectionId",
    allow: false,
    authenticated: true,
    component: CollectionPage,
    exact: true,
  },
  {
    id: "forgot-password",
    name: "Edit",
    path: "/users/password/forgot",
    allow: true,
    authenticated: false,
    component: ForgotPasswordPage,
    exact: true,
  },
  {
    id: "reset-password",
    name: "Edit",
    path: "/users/password/edit",
    allow: true,
    authenticated: false,
    component: ResetPasswordPage,
    exact: true,
  },
  {
    id: "confirm-affiliate",
    name: "Confirm Affiliate Email",
    path: "/affiliates/confirm",
    allow: true,
    authenticated: false,
    component: ConfirmAffiliatePage,
    exact: true,
  },
  {
    id: "network-callback",
    name: "Network Callback",
    path: "/auth/networks/callback",
    allow: true,
    authenticated: false,
    component: NetworkCallbackPage,
    exact: true,
  },
]

const InternalApp = () => {
  const { validate } = React.useContext(AuthContext)
  // Store our loading state (initializing)
  const [isLoaded, setIsLoaded] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)

  const getFirstReferringParams = React.useCallback(() => {
    return new Promise((resolve, reject) => {
      branch.first(function(err, data) {
        if (!!err || !data || !data.referring_link) return reject()
        resolve(JSON.parse(data.data))
      });
    })
  }, [])

  React.useEffect(() => {
    if (isLoaded || isLoading) return
    setIsLoading(true)

    const verifyCurrentUser = async () => {
      try {
        await validate()
      } finally {
        setIsLoaded(true)
        setIsLoading(false)
      }
    }

    const trackAffiliateDownload = (force = false) => {
      return new Promise(async resolve => {
        try {
          // If we don't have install params then we don't want to track this
          const branchData = await getFirstReferringParams()
          // Do a function
          const response = await EventApi.open(branchData)
          const data = response.data
          resolve(data)
        } catch (err) {
          // resolving here since it doesn't matter anyways and
          // we don't wanna throw unhandled errors
          resolve(err)
        }
      })
    }

    verifyCurrentUser()
    trackAffiliateDownload()
  }, [isLoaded, isLoading, validate, getFirstReferringParams])

  return (
    <>
      <ModalConductor />
      <SnackbarConductor />
      {isLoaded ? (
        <Switch>
          {routes.map(({ id, ...rest }) => (
            <AppRoute key={id} {...rest} />
          ))}
          <Redirect to="/login" />
        </Switch>
      ) : null}
    </>
  )
}

function App() {
  return (
    <IntlProvider locale={language || "en"} messages={messages[language]}>
      <AuthProvider>
        <BreadcrumbContextProvider>
          <DownloadContextProvider>
            <HashtagDrawerContextProvider>
              <ClipboardContextProvider>
                <HashtagContextProvider>
                  <CollectionContextProvider>
                    <CaptionContextProvider>
                      <SnackbarContextProvider>
                        <ModalProvider>
                          <InternalApp />
                        </ModalProvider>
                      </SnackbarContextProvider>
                    </CaptionContextProvider>
                  </CollectionContextProvider>
                </HashtagContextProvider>
              </ClipboardContextProvider>
            </HashtagDrawerContextProvider>
          </DownloadContextProvider>
        </BreadcrumbContextProvider>
      </AuthProvider>
    </IntlProvider>
  )
}

export default App
